import React, { useState, useEffect, useRef } from 'react';
import BaseChart from './BaseChart';
import { getAuthToken } from '../../utils/authUtils';
import { toast } from 'react-toastify';
import chartSyncActivator from '../../utils/ChartSyncActivator';
import { formatChartTimeLabel, formatChartTooltipTime, validateDateRange } from '../../utils/DateUtils';

// Активируем синхронизацию графиков при импорте компонента
if (!chartSyncActivator.initialized) {
  chartSyncActivator.initialize();
}

const SpeedChart = ({ vehicle, startDate: propsStartDate, endDate: propsEndDate }) => {
  const abortControllerRef = useRef(null);
  
  // Состояние для хранения дат, которые будут синхронизироваться с localStorage
  const [startDate, setStartDate] = useState(propsStartDate);
  const [endDate, setEndDate] = useState(propsEndDate);
  
  // Функция для обеспечения валидных дат (аналогично TrackMap)
  const ensureValidDate = (dateInput) => {
    try {
      // Если дата уже является объектом Date и она валидна
      if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
        return dateInput;
      }
      
      // Если дата - строка
      if (typeof dateInput === 'string') {
        // Проверяем, не является ли строка пустой или невалидной
        if (!dateInput || dateInput === 'Invalid Date') {
          console.warn('Получена невалидная строка даты:', dateInput);
          return new Date();
        }
        
        // Пробуем стандартный метод
        const newDate = new Date(dateInput);
        if (!isNaN(newDate.getTime())) {
          return newDate;
        }
      }
      
      // Если дата - timestamp (число)
      if (typeof dateInput === 'number') {
        const newDate = new Date(dateInput);
        if (!isNaN(newDate.getTime())) {
          return newDate;
        }
      }
      
      // Если не удалось создать валидную дату, возвращаем текущую
      console.warn('Невозможно создать валидную дату из:', dateInput);
      return new Date();
    } catch (error) {
      console.error('Ошибка при обработке даты:', error, dateInput);
      return new Date();
    }
  };
  
  // Эффект для инициализации дат из localStorage и подписки на их изменение
  useEffect(() => {
    // Функция для чтения дат из localStorage
    const getDateRangeFromLocalStorage = () => {
      try {
        const storedRange = localStorage.getItem('lastDateRange');
        if (storedRange) {
          const parsed = JSON.parse(storedRange);
          return {
            startDate: parsed.startDate ? ensureValidDate(parsed.startDate) : null,
            endDate: parsed.endDate ? ensureValidDate(parsed.endDate) : null,
            updateTimestamp: parsed.updateTimestamp
          };
        }
      } catch (e) {
        console.warn('Ошибка при чтении диапазона дат из localStorage:', e);
      }
      return { startDate: null, endDate: null };
    };
    
    // Обработчик события изменения дат (переработан для соответствия TrackMap)
    const handleDateRangeChanged = (event) => {
      try {
        const { startDate: newStartDate, endDate: newEndDate, period, timestamp } = event.detail;
        
        // Проверяем, не слишком ли недавно мы сами обновили даты
        const now = new Date().getTime();
        const lastUpdate = window.lastDateUpdateTime || 0;
        
        // Если обновление было менее 1 секунды назад и это мы сами его сделали, игнорируем
        if ((now - lastUpdate) < 1000 && window.lastDateUpdateTime === timestamp) {
          console.log('SpeedChart: Игнорируем событие dateRangeChanged - недавнее собственное обновление');
          return;
        }
        
        console.log('SpeedChart: Получено событие изменения диапазона дат:', event.detail);
        
        // Если передан предустановленный период (неделя, месяц и т.д.)
        if (period) {
          console.log('SpeedChart: Обработка предустановленного периода:', period);
          
          // Создаем даты на основе предустановленного периода
          let validStartDate = new Date();
          let validEndDate = new Date();
          
          switch(period) {
            case 'day':
              // Текущий день
              validStartDate.setHours(0, 0, 0, 0);
              break;
            case 'week':
              // Последние 7 дней
              validStartDate = new Date(validEndDate);
              validStartDate.setDate(validEndDate.getDate() - 7);
              validStartDate.setHours(0, 0, 0, 0);
              break;
            case 'month':
              // Последние 30 дней
              validStartDate = new Date(validEndDate);
              validStartDate.setDate(validEndDate.getDate() - 30);
              break;
            case 'year':
              // Последние 365 дней
              validStartDate = new Date(validEndDate);
              validStartDate.setDate(validEndDate.getDate() - 365);
              break;
            default:
              // По умолчанию - неделя
              validStartDate = new Date(validEndDate);
              validStartDate.setDate(validEndDate.getDate() - 7);
          }
          
          // Сохраняем метку времени последнего обновления
          window.lastDateUpdateTime = timestamp || new Date().getTime();
          
          console.log('SpeedChart: Обновление дат на основе периода:', {
            start: validStartDate.toISOString(),
            end: validEndDate.toISOString()
          });
          
          setStartDate(validStartDate);
          setEndDate(validEndDate);
          
          return;
        }
        
        if (!newStartDate || !newEndDate) {
          if (event.detail.forceUpdate) {
            const { startDate: storedStartDate, endDate: storedEndDate } = getDateRangeFromLocalStorage();
            
            if (storedStartDate && storedEndDate) {
              const startChanged = !startDate || 
                Math.abs(storedStartDate.getTime() - startDate.getTime()) > 1000;
              const endChanged = !endDate || 
                Math.abs(storedEndDate.getTime() - endDate.getTime()) > 1000;
                
              if (startChanged || endChanged) {
                console.log('SpeedChart: Обновление дат из localStorage через forceUpdate:', {
                  startDate: storedStartDate.toISOString(),
                  endDate: storedEndDate.toISOString()
                });
                
                setStartDate(storedStartDate);
                setEndDate(storedEndDate);
              }
            }
          } else {
            console.warn('SpeedChart: Получены неполные данные в событии dateRangeChanged:', event.detail);
          }
          return;
        }
        
        // Обрабатываем полученные даты
        const validStartDate = ensureValidDate(newStartDate);
        const validEndDate = ensureValidDate(newEndDate);
        
        // Сохраняем метку времени последнего обновления
        window.lastDateUpdateTime = timestamp || new Date().getTime();
        
        // Проверяем, изменились ли даты
        const startChanged = !startDate || 
          Math.abs(validStartDate.getTime() - startDate.getTime()) > 1000;
        const endChanged = !endDate || 
          Math.abs(validEndDate.getTime() - endDate.getTime()) > 1000;
          
        if (startChanged || endChanged) {
          console.log('SpeedChart: Обновление дат из события:', {
            startDate: validStartDate.toISOString(),
            endDate: validEndDate.toISOString()
          });
          
          setStartDate(validStartDate);
          setEndDate(validEndDate);
        } else {
          console.log('SpeedChart: Даты не изменились, обновление не требуется');
        }
      } catch (error) {
        console.error('SpeedChart: Ошибка при обработке события изменения дат:', error);
      }
    };
    
    // Обработчик события изменения дат внутри текущей вкладки
    const handleDateRangeChangedInTab = (event) => {
      try {
        const { startDate: newStartDate, endDate: newEndDate, timestamp } = event.detail;
        
        // Проверяем, не слишком ли недавно мы сами обновили даты
        const now = new Date().getTime();
        const lastUpdate = window.lastDateUpdateTime || 0;
        
        // Если обновление было менее 1 секунды назад и это мы сами его сделали, игнорируем
        if ((now - lastUpdate) < 1000 && window.lastDateUpdateTime === timestamp) {
          console.log('SpeedChart: Игнорируем событие dateRangeChangedInTab - недавнее собственное обновление');
          return;
        }
        
        console.log('SpeedChart: Получено событие изменения дат в текущей вкладке:', {
          startDate: newStartDate instanceof Date ? newStartDate.toISOString() : newStartDate,
          endDate: newEndDate instanceof Date ? newEndDate.toISOString() : newEndDate
        });
        
        const validStartDate = ensureValidDate(newStartDate);
        const validEndDate = ensureValidDate(newEndDate);
        
        // Проверяем, изменились ли даты
        const startChanged = !startDate || 
          Math.abs(validStartDate.getTime() - startDate.getTime()) > 1000;
        const endChanged = !endDate || 
          Math.abs(validEndDate.getTime() - endDate.getTime()) > 1000;
          
        if (startChanged || endChanged) {
          console.log('SpeedChart: Обновление дат из события в текущей вкладке:', {
            startDate: validStartDate.toISOString(),
            endDate: validEndDate.toISOString()
          });
          
          setStartDate(validStartDate);
          setEndDate(validEndDate);
        }
      } catch (error) {
        console.error('SpeedChart: Ошибка при обработке события изменения дат в текущей вкладке:', error);
      }
    };
    
    // Обработчик события изменения в localStorage 
    const handleStorageChange = (event) => {
      if (event.key === 'lastDateRange') {
        try {
          // Проверяем, не слишком ли недавно мы сами обновили даты
          const now = new Date().getTime();
          const lastUpdate = window.lastDateUpdateTime || 0;
          
          // Если обновление было менее 1 секунды назад, игнорируем
          if ((now - lastUpdate) < 1000) {
            console.log('SpeedChart: Игнорируем событие storage - недавнее собственное обновление');
            return;
          }
          
          console.log('SpeedChart: Обнаружено изменение диапазона дат в localStorage');
          
          const newRange = JSON.parse(event.newValue);
          if (newRange && newRange.startDate && newRange.endDate) {
            const validStartDate = ensureValidDate(newRange.startDate);
            const validEndDate = ensureValidDate(newRange.endDate);
            
            // Проверяем, изменились ли даты
            const startChanged = !startDate || 
              Math.abs(validStartDate.getTime() - startDate.getTime()) > 1000;
            const endChanged = !endDate || 
              Math.abs(validEndDate.getTime() - endDate.getTime()) > 1000;
              
            if (startChanged || endChanged) {
              console.log('SpeedChart: Обновление дат из localStorage:', {
                startDate: validStartDate.toISOString(),
                endDate: validEndDate.toISOString()
              });
              
              setStartDate(validStartDate);
              setEndDate(validEndDate);
            }
          }
        } catch (error) {
          console.warn('SpeedChart: Ошибка при разборе диапазона дат из localStorage:', error);
        }
      }
    };
    
    // При первой загрузке компонента читаем даты из localStorage
    const { startDate: initialStartDate, endDate: initialEndDate } = getDateRangeFromLocalStorage();
    
    // Если даты есть в localStorage и не установлены через пропсы, используем их
    if (initialStartDate && initialEndDate) {
      if (!propsStartDate) setStartDate(initialStartDate);
      if (!propsEndDate) setEndDate(initialEndDate);
      console.log('SpeedChart: Инициализированы даты из localStorage:', {
        startDate: initialStartDate.toISOString(),
        endDate: initialEndDate.toISOString()
      });
    }
    
    // Подписываемся на все события изменения дат
    document.addEventListener('dateRangeChanged', handleDateRangeChanged);
    document.addEventListener('dateRangeChangedInTab', handleDateRangeChangedInTab);
    window.addEventListener('storage', handleStorageChange);
    
    // Отписка при размонтировании
    return () => {
      document.removeEventListener('dateRangeChanged', handleDateRangeChanged);
      document.removeEventListener('dateRangeChangedInTab', handleDateRangeChangedInTab);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [startDate, endDate, propsStartDate, propsEndDate]);
  
  // Обновляем даты из пропсов при их изменении
  useEffect(() => {
    if (propsStartDate) {
      const newStartDate = ensureValidDate(propsStartDate);
      if (!startDate || Math.abs(newStartDate.getTime() - startDate.getTime()) > 1000) {
        console.log('SpeedChart: Обновление startDate из props:', newStartDate.toISOString());
        setStartDate(newStartDate);
      }
    }
    if (propsEndDate) {
      const newEndDate = ensureValidDate(propsEndDate);
      if (!endDate || Math.abs(newEndDate.getTime() - endDate.getTime()) > 1000) {
        console.log('SpeedChart: Обновление endDate из props:', newEndDate.toISOString());
        setEndDate(newEndDate);
      }
    }
  }, [propsStartDate, propsEndDate, startDate, endDate]);
  
  // Добавляем обработчик события изменения текущего транспортного средства 
  useEffect(() => {
    const handleVehicleChanged = (event) => {
      // Проверяем наличие данных о ТС
      if (event.detail && event.detail.vehicle) {
        const newVehicle = event.detail.vehicle;
        
        // Проверяем, отличается ли новое ТС от текущего
        if (vehicle?.imei !== newVehicle.imei) {
          console.log('SpeedChart: Получено событие изменения ТС:', {
            oldImei: vehicle?.imei,
            newImei: newVehicle.imei,
            name: newVehicle.name || 'Неизвестно'
          });
          
          // Отменяем текущий запрос, если он есть
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          
          // Обнуляем данные для предотвращения отображения старых данных
          setStartDate(null);
          setEndDate(null);
        }
      }
    };
    
    // Подписываемся на событие изменения ТС
    document.addEventListener('changeCurrentVehicle', handleVehicleChanged);
    
    return () => {
      document.removeEventListener('changeCurrentVehicle', handleVehicleChanged);
    };
  }, [vehicle]);
  
  // Подписка на событие обновления данных
  useEffect(() => {
    const handleForceUpdate = (event) => {
      if (event.detail) {
        // Проверяем, есть ли в событии информация о транспортном средстве
        if (event.detail.vehicle && vehicle?.imei !== event.detail.vehicle.imei) {
          console.log('SpeedChart: Получено новое ТС из события forceVehicleUpdate:', {
            oldImei: vehicle?.imei,
            newImei: event.detail.vehicle.imei,
            vehicleName: event.detail.vehicle.name || 'Неизвестно'
          });
          
          // Не вызываем fetchSpeedData здесь, т.к. он будет вызван автоматически 
          // при изменении vehicle через эффект, который следит за vehicle?.imei
          return;
        }
        
        // Если есть конкретные даты в событии, используем их
        if (event.detail.startDate && event.detail.endDate) {
          const validStartDate = ensureValidDate(event.detail.startDate);
          const validEndDate = ensureValidDate(event.detail.endDate);
          
          console.log('SpeedChart: Получены даты из события forceVehicleUpdate:', {
            startDate: validStartDate.toISOString(),
            endDate: validEndDate.toISOString()
          });
          
          // Проверяем, изменились ли даты
          const startChanged = !startDate || 
            Math.abs(validStartDate.getTime() - startDate.getTime()) > 1000;
          const endChanged = !endDate || 
            Math.abs(validEndDate.getTime() - endDate.getTime()) > 1000;
            
          if (startChanged || endChanged) {
            setStartDate(validStartDate);
            setEndDate(validEndDate);
            
            // fetchSpeedData вызовется автоматически из-за изменения зависимостей
            return;
          }
        }
        
        // Предотвращаем множественные запросы в течение короткого времени
        if (abortControllerRef.current) {
          console.log('SpeedChart: Отмена предыдущего запроса для ТС', vehicle?.name || vehicle?.imei);
          abortControllerRef.current.abort();
        }
        
        // Используем setTimeout для снижения нагрузки при множественных событиях
        setTimeout(() => {
          if (vehicle?.imei) {
            console.log(`SpeedChart: Выполнение forceVehicleUpdate для ТС ${vehicle.name || vehicle.imei}`);
            // Не вызываем fetchSpeedData напрямую - BaseChart управляет загрузкой через useChartState
            // fetchSpeedData будет вызван автоматически при изменении данных
          }
        }, 300);
      }
    };
    
    document.addEventListener('forceVehicleUpdate', handleForceUpdate);
    
    return () => {
      document.removeEventListener('forceVehicleUpdate', handleForceUpdate);
    };
  }, [vehicle, startDate, endDate]);
  
  // Функция для предварительной обработки данных скорости
  const preprocessSpeedData = (rawData) => {
    // Проверяем наличие и корректность данных
    if (!Array.isArray(rawData) || rawData.length === 0) {
      return { values: [], labels: [] };
    }
    
    // Сортируем по времени
    const sortedData = [...rawData].sort((a, b) => {
      // Поддерживаем оба формата времени: time и _time
      const timeFieldA = a._time || a.time;
      const timeFieldB = b._time || b.time;
      
      const timeA = new Date(timeFieldA).getTime();
      const timeB = new Date(timeFieldB).getTime();
      return timeA - timeB;
    });
    
    // Выводим информацию о структуре данных
    if (sortedData.length > 0) {
      const firstItem = sortedData[0];
      console.log(`Структура данных точки:`, 
                  `время: ${firstItem._time || firstItem.time}`, 
                  `скорость: ${firstItem.value !== undefined ? firstItem.value : 'не найдено'}`);
    }
    
    // Отключено прореживание данных, чтобы отображать все точки
    // (раскомментируйте код ниже, если нужно включить прореживание)
    let processedData = sortedData;
    /*
    // Прореживаем данные, если их слишком много (более 1000 точек)
    if (sortedData.length > 1000) {
      const step = Math.ceil(sortedData.length / 1000);
      processedData = sortedData.filter((_, index) => index % step === 0);
      console.log(`Прореживание данных: ${sortedData.length} -> ${processedData.length} точек`);
    }
    */
    
    // Преобразуем данные в формат для графика
    const values = [];
    const labels = [];
    
    for (const item of processedData) {
      // Получаем значение времени (поддерживаем оба поля: time и _time)
      const timeField = item._time || item.time;
      if (!timeField) {
        console.warn('Пропуск элемента без времени:', item);
        continue;
      }
      
      const timeValue = new Date(timeField);
      
      // Получаем значение скорости
      let speedValue = null;
      
      // В новом формате скорость хранится в поле value
      if (typeof item.value === 'number') {
        speedValue = item.value;
      }
      // Для обратной совместимости
      else if (typeof item.speed === 'number') {
        speedValue = item.speed;
      }
      
      // Если не нашли подходящего поля, пропускаем точку
      if (speedValue === null) {
        console.warn('Пропуск элемента с неизвестным форматом данных скорости:', item);
        continue;
      }
      
      // Добавляем точку в результаты
      values.push(speedValue);
      labels.push(timeValue);
    }
    
    return { values, labels };
  };
  
  // Форматирование подписей осей
  const formatSpeedLabel = (value) => `${value} км/ч`;
  
  // Форматирование временных меток на оси X
  const formatTimeLabel = (dateTime) => {
    return formatChartTimeLabel(dateTime, startDate, endDate);
  };
  
  // Получение максимальной и минимальной скорости для подсветки нарушений
  const getChartOptions = () => {
    const options = {
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (tooltipItem) => formatSpeedLabel(tooltipItem.value),
          title: (tooltipItems) => {
            if (tooltipItems.length > 0) {
              const dateTime = tooltipItems[0].xLabel;
              if (dateTime && dateTime instanceof Date) {
                return formatChartTooltipTime(dateTime);
              }
            }
            return '';
          }
        }
      },
      elements: {
        point: {
          radius: 0, // Полностью отключаем точки для соответствия скриншоту
          hitRadius: 10, // Область для взаимодействия
          hoverRadius: 5, // Размер точки при наведении
        },
        line: {
          tension: 0, // Убираем сглаживание для получения прямых линий
          borderWidth: 2 // Устанавливаем толщину линии
        }
      },
      annotation: {
        annotations: []
      }
    };
    
    // Добавляем линию максимальной разрешенной скорости, если известна для этого транспорта
    if (vehicle && vehicle.maxSpeed && vehicle.maxSpeed > 0) {
      options.annotation.annotations.push({
        type: 'line',
        mode: 'horizontal',
        scaleID: 'y-axis-0',
        value: vehicle.maxSpeed,
        borderColor: 'rgba(255, 0, 0, 0.5)',
        borderWidth: 1,
        label: {
          content: `Макс. ${vehicle.maxSpeed} км/ч`,
          enabled: true,
          position: 'right'
        }
      });
    }
    
    return options;
  };
  
  const fetchSpeedData = async (vehicleData, startDateObj, endDateObj) => {
    // Используем переданные параметры или fallback на локальные состояния
    const vehicleToUse = vehicleData || vehicle;
    const useStartDate = startDateObj || startDate;
    const useEndDate = endDateObj || endDate;
    
    // Проверяем наличие ТС
    if (!vehicleToUse || !vehicleToUse.imei) {
      console.error('Не удалось получить IMEI транспортного средства');
      throw new Error('Для получения данных о скорости необходимо указать IMEI транспортного средства');
    }
    
    // Проверяем даты
    if (!useStartDate || !useEndDate) {
      console.error('Не указаны даты для запроса данных о скорости');
      throw new Error('Необходимо указать период для получения данных о скорости');
    }
    
    // Отменяем предыдущий запрос, если он выполняется
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Создаем новый контроллер прерывания
    abortControllerRef.current = new AbortController();
    
    // Убедимся, что даты являются объектами Date
    const startObj = useStartDate instanceof Date ? useStartDate : new Date(useStartDate);
    const endObj = useEndDate instanceof Date ? useEndDate : new Date(useEndDate);
    
    // Проверяем и исправляем диапазон дат
    const { startDate: validStartDate, endDate: validEndDate } = validateDateRange(startObj, endObj);
    
    // Форматируем даты для API
    const formattedStartDate = validStartDate.toISOString();
    const formattedEndDate = validEndDate.toISOString();
    
    try {
      console.log(`Запрос данных о скорости для ${vehicleToUse.name || vehicleToUse.imei}:`, {
        imei: vehicleToUse.imei,
        startDate: formattedStartDate,
        endDate: formattedEndDate
      });
      
      // Формируем URL для запроса
      const apiUrl = `/api/telemetry/v3/${vehicleToUse.imei}/speed?startTime=${formattedStartDate}&endTime=${formattedEndDate}`;
      
      // Получаем токен авторизации
      const authToken = getAuthToken();
      
      // Подготавливаем заголовки
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      
      // Если токен найден, добавляем его в заголовки
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      
      // Выполняем запрос
      const response = await fetch(apiUrl, { 
        method: 'GET',
        headers: headers,
        signal: abortControllerRef.current.signal
      });
      
      // Проверяем ответ
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Ошибка API: ${response.status} - ${errorText || response.statusText}`);
      }
      
      const responseData = await response.json();
      
      console.log('Получен ответ от API:', responseData);
      
      // Проверяем формат данных (новый формат с метаданными)
      if (responseData && typeof responseData === 'object') {
        // Проверяем есть ли поле points, указывающее на новую структуру API
        if (Array.isArray(responseData.points)) {
          // Это новый формат с полем points и метриками
          
          // Если в points нет данных
          if (responseData.points.length === 0) {
            console.warn(`API вернул пустой массив данных о скорости для ${vehicleToUse.name || vehicleToUse.imei}`);
            return { labels: [], datasets: [] };
          }
          
          console.log(`Получено ${responseData.points.length} точек для графика скорости ${vehicleToUse.name || vehicleToUse.imei}`);
          
          // Изучаем структуру первого элемента для отладки
          if (responseData.points.length > 0) {
            console.log('Пример структуры данных:', responseData.points[0]);
          }
          
          // Обрабатываем точки из нового формата
          const processedData = preprocessSpeedData(responseData.points);
          
          if (processedData.values.length === 0) {
            console.warn('После обработки данных не осталось точек для графика');
            return { labels: [], datasets: [] };
          } else {
            // Возвращаем данные в формате Chart.js
            return {
              labels: processedData.labels,
              datasets: [{
                label: 'Скорость',
                data: processedData.values,
                fill: false,
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderColor: 'rgb(75, 192, 192)',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 5,
                tension: 0,
              }]
            };
          }
        } else if (Array.isArray(responseData)) {
          // Это старый формат, где ответ - просто массив точек
          
          if (responseData.length === 0) {
            console.warn(`API вернул пустой массив данных о скорости для ${vehicleToUse.name || vehicleToUse.imei}`);
            return { labels: [], datasets: [] };
          }
          
          console.log(`Получено ${responseData.length} точек для графика скорости ${vehicleToUse.name || vehicleToUse.imei}`);
          
          // Изучаем структуру первого элемента для отладки
          if (responseData.length > 0) {
            console.log('Пример структуры данных:', responseData[0]);
          }
          
          // Фильтруем и подготавливаем данные
          const processedData = preprocessSpeedData(responseData);
          
          if (processedData.values.length === 0) {
            console.warn('После обработки данных не осталось точек для графика');
            return { labels: [], datasets: [] };
          } else {
            // Возвращаем данные в формате Chart.js
            return {
              labels: processedData.labels,
              datasets: [{
                label: 'Скорость',
                data: processedData.values,
                fill: false,
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderColor: 'rgb(75, 192, 192)',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 5,
                tension: 0,
              }]
            };
          }
        } else {
          // Неизвестный формат
          console.error('API вернул неизвестный формат данных:', responseData);
          return { labels: [], datasets: [] };
        }
      } else {
        console.error('API вернул неверный формат данных:', responseData);
        return { labels: [], datasets: [] };
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Запрос данных о скорости был отменен');
        return { labels: [], datasets: [] };
      } else {
        console.error('Ошибка при загрузке данных скорости:', err);
        // Показываем уведомление об ошибке
        toast.error(`Ошибка загрузки данных о скорости: ${err.message}`, {
          position: "top-right",
          autoClose: 5000
        });
        throw err; // Пробрасываем ошибку для BaseChart
      }
    }
  };
  
  return (
    <>
      {/* {debugInfo && (
        <div className="chart-debug-info" style={{
          padding: '8px 12px',
          margin: '0 0 10px 0',
          backgroundColor: '#fff3cd',
          color: '#856404',
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          <strong>Отладка:</strong> {debugInfo}
        </div>
      )} */}
      
      {/* Блок метрик */}
      {/* {chartData.length > 0 && (
        <div className="speed-metrics" style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '10px 15px',
          marginBottom: '15px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div className="metric-item">
            <div style={{ fontSize: '12px', color: '#6c757d' }}>Макс. скорость</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
              {formatNumber(metrics.maxSpeed)} км/ч
            </div>
          </div>
          <div className="metric-item">
            <div style={{ fontSize: '12px', color: '#6c757d' }}>Средняя скорость</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
              {formatNumber(metrics.avgSpeed)} км/ч
            </div>
          </div>
          <div className="metric-item">
            <div style={{ fontSize: '12px', color: '#6c757d' }}>Точек данных</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
              {metrics.totalPoints || chartData.length}
            </div>
          </div>
        </div>
      )} */}
      
      <BaseChart
        title="График скорости"
        vehicle={vehicle}
        startDate={propsStartDate || startDate}
        endDate={propsEndDate || endDate}
        color="rgb(75, 192, 192)"
        fetchData={fetchSpeedData}
        formatTooltipLabel={formatSpeedLabel}
        formatYAxisLabel={formatSpeedLabel}
        formatXAxisLabel={formatTimeLabel}
        emptyDataMessage="Нет данных о скорости за выбранный период"
        options={getChartOptions()}
        reportType="speed"
      />
    </>
  );
};

export default SpeedChart; 